import { memo, useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MONTH_YEAR_DISPLAY_FORMAT } from '@/helpers';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import {
  DateRangeCalendar,
  DateRangeWithOptionalEnd,
} from '@/modules/common/components/molecules/date/date-range-calendar';

import { DATE_NAV_DEBOUNCE_MS, SCROLL_END_DEBOUNCE_MS } from '../constants';
import usePlannerStore from '../store';

export const DateSwitcherHeader = memo(() => {
  const { t } = useTranslation('authenticated.planner');
  const nextDateClick = usePlannerStore(store => store.nextDateClick);
  const prevDateClick = usePlannerStore(store => store.prevDateClick);
  const setToday = usePlannerStore(store => store.setToday);
  const scrollToDate = usePlannerStore(store => store.scrollToDate);

  const debouncedPrevClick = useDebouncedCallback(prevDateClick, DATE_NAV_DEBOUNCE_MS);
  const debouncedNextClick = useDebouncedCallback(nextDateClick, DATE_NAV_DEBOUNCE_MS);
  const debouncedSetToday = useDebouncedCallback(setToday, DATE_NAV_DEBOUNCE_MS);

  // Derive display range from firstVisibleDate but only commit it to
  // rendered state once the planner container stops scrolling.
  const [displayRange, setDisplayRange] = useState(() =>
    dayjs(usePlannerStore.getState().firstVisibleDate)
      .add(10, 'days')
      .format(MONTH_YEAR_DISPLAY_FORMAT)
  );
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const unsub = usePlannerStore.subscribe((state, prevState) => {
      if (state.firstVisibleDate === prevState.firstVisibleDate) return;

      const next = dayjs(state.firstVisibleDate).add(10, 'days').format(MONTH_YEAR_DISPLAY_FORMAT);

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        idleTimerRef.current = null;
        setDisplayRange(next);
      }, SCROLL_END_DEBOUNCE_MS);
    });

    return () => {
      unsub();
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  const [open, setOpen] = useState(false);

  const formatToValue = (dateRange: DateRangeWithOptionalEnd): dayjs.Dayjs =>
    dayjs(dateRange?.from);

  const handleChange = (dateRange: DateRange) => {
    scrollToDate(dayjs(formatToValue(dateRange)));
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-1">
      <div className="flex h-9 items-center rounded-md border">
        <Button variant="ghost" size="sm" onClick={debouncedPrevClick} className="h-8">
          <ChevronLeft size={16} />
        </Button>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            className={cn('h-9 px-2 py-1 rounded text-sm text-left', open && 'bg-muted')}
          >
            <div className="flex items-center">
              <p className="whitespace-nowrap text-sm font-medium">{displayRange}</p>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto space-y-3 p-0" side="bottom" align="start">
            <DateSwitcherCalendar onChange={handleChange} />
          </PopoverContent>
        </Popover>

        <Button variant="ghost" size="sm" onClick={debouncedNextClick} className="h-8">
          <ChevronRight size={16} />
        </Button>
      </div>

      <Button variant="outline" size="sm" onClick={debouncedSetToday}>
        {t('today')}
      </Button>
    </div>
  );
});

/**
 * Calendar content extracted to its own component so the firstVisibleDate
 * subscription only activates when the popover is mounted (open).
 * This prevents the parent DateSwitcherHeader from re-rendering every
 * scroll frame just because it holds a reference to firstVisibleDate.
 */
const DateSwitcherCalendar = memo(({ onChange }: { onChange: (dateRange: DateRange) => void }) => {
  const firstVisibleDate = usePlannerStore(store => store.firstVisibleDate);

  const value: DateRangeWithOptionalEnd = {
    from: firstVisibleDate ? firstVisibleDate.toDate() : new Date(),
  };

  return <DateRangeCalendar value={value} onChange={onChange} disableRangeSelection />;
});
